import { useEffect, useMemo, useRef, useState } from 'react';
import { fetchConsentStatus, revokeConsent, type ConsentStatusResponse } from '../consentApi';
import { HopperIcon } from '../ui/icons/HopperIcon';
import {
  checkSlugAvailability,
  fetchProfileSettings,
  ProfileSettingsApiError,
  saveProfileSettings,
  uploadProfileAvatar,
  type ProductNotificationPreferences,
  type ProfileSettingsData,
} from './profileSettingsApi';
import { ProfileAvatar } from './ProfileAvatar';
import '../profile-settings.css';
import '../tz5-owner-qa.css';

type SlugState = 'idle' | 'checking' | 'available' | 'taken' | 'invalid';

type ProfileSettingsScreenProps = {
  onBack: () => void;
  onSaved: (profile: ProfileSettingsData) => void;
};

const SLUG_PATTERN = /^[a-z0-9_]{3,30}$/;
const RESERVED_SLUGS = new Set(['settings']);
const MAX_AVATAR_BYTES = 2 * 1024 * 1024;
const AVATAR_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

function Toggle({ checked, label, description, onChange }: {
  checked: boolean;
  label: string;
  description?: string;
  onChange: (checked: boolean) => void;
}) {
  return (
    <div className="profile-settings-toggle-row">
      <div>
        <strong>{label}</strong>
        {description && <span>{description}</span>}
      </div>
      <button
        className={checked ? 'profile-settings-switch is-on' : 'profile-settings-switch'}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={() => onChange(!checked)}
      >
        <span />
      </button>
    </div>
  );
}

export function ProfileSettingsScreen({ onBack, onSaved }: ProfileSettingsScreenProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [profile, setProfile] = useState<ProfileSettingsData | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [slug, setSlug] = useState('');
  const [bio, setBio] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [consent, setConsent] = useState<ConsentStatusResponse | null>(null);
  const [revokingConsent, setRevokingConsent] = useState(false);
  const [preferences, setPreferences] = useState<ProductNotificationPreferences>({
    visit_impressions: true,
    personal_offers: true,
    saved_place_updates: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [slugState, setSlugState] = useState<SlugState>('idle');
  const [slugMessage, setSlugMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    void Promise.all([
      fetchProfileSettings(),
      fetchConsentStatus().catch(() => null),
    ])
      .then(([response, consentResponse]) => {
        if (cancelled) return;
        setProfile(response.data);
        setDisplayName(response.data.display_name);
        setSlug(response.data.slug ?? '');
        setBio(response.data.bio ?? '');
        setIsPublic(response.data.is_public);
        setPreferences(response.data.notification_preferences);
        setConsent(consentResponse);
      })
      .catch(() => {
        if (!cancelled) setError('Не удалось загрузить настройки профиля.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    setSuccess(null);

    if (slug === '') {
      setSlugState('idle');
      setSlugMessage(null);
      return undefined;
    }

    if (!SLUG_PATTERN.test(slug)) {
      setSlugState('invalid');
      setSlugMessage(slug.length > 0 && (slug.length < 3 || slug.length > 30)
        ? 'От 3 до 30 символов. Только латинские буквы, цифры и _.'
        : 'Только латинские буквы, цифры и _.');
      return undefined;
    }

    if (RESERVED_SLUGS.has(slug)) {
      setSlugState('taken');
      setSlugMessage('Этот никнейм недоступен.');
      return undefined;
    }

    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      setSlugState('checking');
      setSlugMessage('Проверяем никнейм…');

      void checkSlugAvailability(slug, controller.signal)
        .then((result) => {
          if (!result.valid) {
            setSlugState('invalid');
            setSlugMessage(result.message);
          } else if (result.available) {
            setSlugState('available');
            setSlugMessage('Свободен');
          } else {
            setSlugState('taken');
            setSlugMessage(result.message);
          }
        })
        .catch((reason: unknown) => {
          if (reason instanceof DOMException && reason.name === 'AbortError') return;
          setSlugState('idle');
          setSlugMessage('Не удалось проверить никнейм. Повторите позже.');
        });
    }, 350);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [slug]);

  const profileUrl = useMemo(() => {
    if (!profile || slug === '') return '';
    return `${window.location.origin}/profile/${encodeURIComponent(slug)}`;
  }, [profile, slug]);

  const updatePreference = (key: keyof ProductNotificationPreferences, checked: boolean) => {
    setPreferences((current) => ({ ...current, [key]: checked }));
    setSuccess(null);
  };

  const pickAvatar = () => fileInputRef.current?.click();

  const uploadAvatar = async (file: File | undefined) => {
    if (!file) return;

    setAvatarError(null);
    setSuccess(null);

    if (!AVATAR_TYPES.has(file.type)) {
      setAvatarError('Поддерживаются JPEG, PNG и WebP.');
      return;
    }

    if (file.size > MAX_AVATAR_BYTES) {
      setAvatarError('Размер изображения не должен превышать 2 МБ.');
      return;
    }

    setUploading(true);
    try {
      const uploaded = await uploadProfileAvatar(file);
      const fresh = await fetchProfileSettings();
      setProfile(fresh.data);
      if (uploaded.avatarUrl && !fresh.data.avatar_url) {
        setProfile({ ...fresh.data, avatar_url: uploaded.avatarUrl });
      }
      setSuccess('Аватар обновлён.');
    } catch {
      setAvatarError('Не удалось загрузить аватар. Попробуйте ещё раз.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const save = async () => {
    const nextName = displayName.trim();
    const nextBio = bio.trim();
    setError(null);
    setSuccess(null);

    if (!nextName) {
      setError('Укажите отображаемое имя.');
      return;
    }

    if (nextName.length > 50) {
      setError('Имя должно быть не длиннее 50 символов.');
      return;
    }

    if (nextBio.length > 255) {
      setError('Описание должно быть не длиннее 255 символов.');
      return;
    }

    if (slug !== '' && slugState !== 'available') {
      setError(slugMessage ?? 'Проверьте никнейм перед сохранением.');
      return;
    }

    setSaving(true);
    try {
      const response = await saveProfileSettings({
        display_name: nextName,
        slug: slug || null,
        bio: nextBio || null,
        is_public: isPublic,
        notification_preferences: preferences,
      });
      setProfile(response.data);
      setDisplayName(response.data.display_name);
      setSlug(response.data.slug ?? '');
      setBio(response.data.bio ?? '');
      setIsPublic(response.data.is_public);
      setPreferences(response.data.notification_preferences);
      setSuccess('Настройки сохранены.');
      onSaved(response.data);
    } catch (reason: unknown) {
      if (reason instanceof ProfileSettingsApiError) {
        const firstFieldError = Object.values(reason.errors)[0]?.[0];
        setError(firstFieldError ?? 'Не удалось сохранить настройки.');
      } else {
        setError('Не удалось сохранить настройки.');
      }
    } finally {
      setSaving(false);
    }
  };

  const revokePrivacyConsent = async () => {
    if (!consent?.data.has_consent || revokingConsent) return;

    setError(null);
    setSuccess(null);
    setRevokingConsent(true);

    try {
      await revokeConsent();
      setConsent((current) => current ? {
        data: {
          ...current.data,
          has_consent: false,
        },
      } : current);
      setSuccess('Согласие отозвано. Сканирование недоступно до нового согласия.');
    } catch {
      setError('Не удалось отозвать согласие. Повторите позже.');
    } finally {
      setRevokingConsent(false);
    }
  };

  if (loading) {
    return (
      <main className="profile-settings-page" aria-label="Настройки профиля">
        <div className="profile-settings-shell profile-settings-loading" role="status">
          <span />
          <span />
          <span />
        </div>
      </main>
    );
  }

  if (!profile) {
    return (
      <main className="profile-settings-page" aria-label="Настройки профиля">
        <section className="profile-settings-shell profile-settings-state">
          <HopperIcon name="error" size={28} />
          <h1>Настройки недоступны</h1>
          <p>{error ?? 'Попробуйте обновить страницу.'}</p>
          <button type="button" className="profile-settings-primary" onClick={onBack}>Вернуться в профиль</button>
        </section>
      </main>
    );
  }

  return (
    <main className="profile-settings-page" aria-label="Настройки профиля">
      <div className="profile-settings-shell">
        <header className="profile-settings-header">
          <button className="profile-settings-back" type="button" onClick={onBack} aria-label="Вернуться в профиль">
            <HopperIcon name="arrow-left" size={20} />
          </button>
          <div>
            <span>Профиль гостя</span>
            <h1>Настройки профиля</h1>
            <p>Имя, описание, публичная ссылка, аватар и ваши предпочтения.</p>
          </div>
        </header>

        {(error || success) && (
          <div className={error ? 'profile-settings-flash is-error' : 'profile-settings-flash is-success'} role="status">
            <HopperIcon name={error ? 'error' : 'check-circle'} size={18} />
            <span>{error ?? success}</span>
          </div>
        )}

        <section className="profile-settings-card profile-settings-identity">
          <div className="profile-settings-card-heading">
            <div>
              <h2>Основные данные</h2>
              <p>Так вас будут видеть другие гости Хоппера.</p>
            </div>
          </div>

          <div className="profile-settings-avatar-row">
            <ProfileAvatar displayName={displayName} avatarUrl={profile.avatar_url} size={120} />
            <div>
              <button type="button" className="profile-settings-secondary" disabled={uploading} onClick={pickAvatar}>
                <HopperIcon name="camera" size={18} />
                {uploading ? 'Загружаем…' : 'Изменить аватар'}
              </button>
              <span>JPEG, PNG или WebP · до 2 МБ</span>
              {avatarError && <strong className="profile-settings-field-error">{avatarError}</strong>}
              <input
                ref={fileInputRef}
                className="profile-settings-file-input"
                type="file"
                accept="image/jpeg,image/png,image/webp"
                onChange={(event) => void uploadAvatar(event.target.files?.[0])}
              />
            </div>
          </div>

          <label className="profile-settings-field">
            <span>Отображаемое имя</span>
            <input
              type="text"
              value={displayName}
              maxLength={50}
              required
              onChange={(event) => { setDisplayName(event.target.value); setSuccess(null); }}
              placeholder="Как к вам обращаться"
            />
            <small>{displayName.length}/50</small>
          </label>

          <label className="profile-settings-field">
            <span>О себе</span>
            <textarea
              value={bio}
              maxLength={255}
              rows={4}
              onChange={(event) => { setBio(event.target.value); setSuccess(null); }}
              placeholder="Коротко о себе и любимых местах"
            />
            <small>{bio.length}/255</small>
          </label>
        </section>

        <section className="profile-settings-card">
          <div className="profile-settings-card-heading">
            <div>
              <h2>Ссылка на профиль</h2>
              <p>Уникальный никнейм делает публичную ссылку короткой и запоминающейся.</p>
            </div>
            <HopperIcon name="globe" size={22} />
          </div>

          <label className="profile-settings-field">
            <span>Никнейм</span>
            <div className="profile-settings-slug-input">
              <span>{window.location.host}/profile/</span>
              <input
                type="text"
                value={slug}
                maxLength={30}
                spellCheck={false}
                autoCapitalize="none"
                autoCorrect="off"
                onChange={(event) => setSlug(event.target.value)}
                placeholder="dmitrii_hopper"
              />
              {slugState === 'available' && <HopperIcon name="check" size={18} />}
              {(slugState === 'taken' || slugState === 'invalid') && <HopperIcon name="x-circle" size={18} />}
            </div>
          </label>

          {slugMessage && (
            <div className={`profile-settings-slug-status is-${slugState}`}>
              {slugState === 'available' && <HopperIcon name="check-circle" size={16} />}
              {(slugState === 'taken' || slugState === 'invalid') && <HopperIcon name="x-circle" size={16} />}
              {slugState === 'checking' && <HopperIcon name="spinner" size={16} />}
              <span>{slugMessage}</span>
            </div>
          )}

          {slug === '' && <p className="profile-settings-help">Добавьте никнейм, чтобы получить короткую публичную ссылку.</p>}

          {slug !== '' && (
            <div className="profile-settings-current-link">
              <span>Ссылка по никнейму</span>
              <code>{profileUrl}</code>
            </div>
          )}
          <button
            className="profile-settings-secondary profile-settings-open-profile"
            type="button"
            onClick={() => window.location.assign(profile.profile_path)}
          >
            Открыть публичный профиль
          </button>
        </section>

        <section className="profile-settings-card">
          <div className="profile-settings-card-heading">
            <div>
              <h2>Приватность</h2>
              <p>Управляйте видимостью профиля и действующим согласием.</p>
            </div>
            <HopperIcon name={isPublic ? 'eye' : 'eye-off'} size={22} />
          </div>

          <Toggle
            checked={isPublic}
            label="Публичный профиль"
            description={isPublic
              ? 'Профиль может отображаться в поиске и рейтингах.'
              : 'Профиль скрыт из поиска и рейтингов. По прямой ссылке другие гости увидят пометку о скрытом профиле.'}
            onChange={(checked) => { setIsPublic(checked); setSuccess(null); }}
          />

          {consent && (
            <div className="profile-settings-consent">
              <div>
                <strong>{consent.data.has_consent ? 'Согласие действует' : 'Согласие отозвано'}</strong>
                <span>Версия {consent.data.current_version}</span>
              </div>
              {consent.data.has_consent && (
                <button
                  type="button"
                  className="profile-settings-secondary"
                  disabled={revokingConsent}
                  onClick={() => void revokePrivacyConsent()}
                >
                  {revokingConsent ? 'Отзываем…' : 'Отозвать согласие'}
                </button>
              )}
            </div>
          )}
        </section>

        <section className="profile-settings-card">
          <div className="profile-settings-card-heading">
            <div>
              <h2>Уведомления</h2>
              <p>Выберите события, о которых хотите узнавать.</p>
            </div>
            <HopperIcon name="bell" size={22} />
          </div>

          <div className="profile-settings-toggle-list">
            <Toggle
              checked={preferences.visit_impressions}
              label="Новые впечатления о моих визитах"
              onChange={(checked) => updatePreference('visit_impressions', checked)}
            />
            <Toggle
              checked={preferences.personal_offers}
              label="Персональные предложения от заведений"
              onChange={(checked) => updatePreference('personal_offers', checked)}
            />
            <Toggle
              checked={preferences.saved_place_updates}
              label="Изменения в моих сохранённых местах"
              onChange={(checked) => updatePreference('saved_place_updates', checked)}
            />
          </div>
        </section>

        <div className="profile-settings-actions">
          <button className="profile-settings-secondary" type="button" onClick={onBack}>Отмена</button>
          <button
            className="profile-settings-primary"
            type="button"
            disabled={saving || uploading || slugState === 'checking' || slugState === 'invalid' || slugState === 'taken'}
            onClick={() => void save()}
          >
            <HopperIcon name="save" size={18} />
            {saving ? 'Сохраняем…' : 'Сохранить изменения'}
          </button>
        </div>
      </div>
    </main>
  );
}
